import {
  alpha,
  buttonBaseClasses,
  buttonClasses,
  iconButtonClasses,
  keyframes,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { getNavFloatShadow } from 'src/components/Navbar/DesktopDrawer/desktop-drawer.styles';
import { GLASS_BLUR, SUPPORTS_BACKDROP_FILTER } from 'src/constants/glass';
import type { ModalActionKind, ModalSize } from './modal-container.types';

const DISABLED_OPACITY = 0.45;

const MODAL_WIDTH_BY_SIZE: Record<ModalSize, string> = {
  small: '27.5rem',
  medium: '35rem',
  large: '41.25rem',
};

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const REDUCED_MOTION = `@media ${REDUCED_MOTION_QUERY}`;

const MODAL_ENTER_MS = 180;

const BACKDROP_ENTER_MS = 160;

const modalRise = keyframes`
  from {
    transform: translateY(0.5rem) scale(0.985);
  }
`;

export const getModalTransitionDurations = (prefersReducedMotion: boolean) =>
  prefersReducedMotion
    ? { dialog: 0, backdrop: 0 }
    : {
        dialog: { enter: MODAL_ENTER_MS, exit: 0 },
        backdrop: { enter: BACKDROP_ENTER_MS, exit: 0 },
      };

export const getModalPaperSx =
  (size: ModalSize, fullScreen: boolean): SxProps<Theme> =>
  (theme) => {
    const { modalSurface, outlineVariant } = theme.palette.custom.OV.sys;
    const { white } = theme.palette.common;

    return {
      color: theme.palette.custom.OV.sys.onSurface,
      background: `linear-gradient(180deg, ${alpha(white, 0.025)}, transparent 7.5rem), ${modalSurface}`,
      boxShadow: `inset 0 1px 0 ${alpha(white, 0.045)}, ${getNavFloatShadow(theme)}`,
      animation: `${modalRise} ${MODAL_ENTER_MS}ms ${theme.transitions.easing.easeOut}`,
      [REDUCED_MOTION]: { animation: 'none' },
      ...(fullScreen
        ? {}
        : {
            width: MODAL_WIDTH_BY_SIZE[size],
            border: `1px solid ${alpha(outlineVariant, 0.7)}`,
            borderRadius: '1rem',
          }),
    };
  };

export const modalTitleSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  color: 'custom.OV.sys.secondaryText',
};

const getKeyboardFocusRingSx = (theme: Theme): SystemStyleObject<Theme> => ({
  [`&.${buttonBaseClasses.focusVisible}`]: {
    outline: `0.125rem solid ${theme.palette.custom.OV.sys.primary}`,
    outlineOffset: '0.0625rem',
  },
});

export const modalIconButtonSx: SxProps<Theme> = (theme) => ({
  width: '1.75rem',
  height: '1.75rem',
  borderRadius: '0.4375rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '&:hover': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: theme.palette.custom.OV.sys.onBackground,
  },
  [`&.${iconButtonClasses.disabled}`]: {
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    opacity: DISABLED_OPACITY,
  },
  ...getKeyboardFocusRingSx(theme),
});

const MODAL_HEADER_HEIGHT = '3.5rem';

const getEdgeTransition = (theme: Theme, properties: string[]) =>
  theme.transitions.create(properties, { duration: theme.transitions.duration.shortest });

export const getModalHeaderSx =
  (isScrolled: boolean): SxProps<Theme> =>
  (theme) => {
    const { modalSurface, outlineVariant } = theme.palette.custom.OV.sys;

    return {
      position: 'relative',
      zIndex: 2,
      height: MODAL_HEADER_HEIGHT,
      boxSizing: 'border-box',
      cursor: 'default',
      userSelect: 'none',
      borderBottom: '1px solid transparent',
      transition: getEdgeTransition(theme, ['background-color', 'border-color']),
      [REDUCED_MOTION]: { transition: 'none' },
      ...(isScrolled && {
        backgroundColor: alpha(modalSurface, 0.8),
        backdropFilter: GLASS_BLUR,
        WebkitBackdropFilter: GLASS_BLUR,
        borderBottomColor: alpha(outlineVariant, 0.7),
        [SUPPORTS_BACKDROP_FILTER]: { backgroundColor: modalSurface },
      }),
    };
  };

export const modalStepSx: SxProps<Theme> = { flex: '1 1 auto', minHeight: 0 };

// The body sits outside the header yet starts under it, so content scrolls behind the frosted
// header while an elastic overscroll pulls only the body, never the header.
export const modalScrollAreaSx: SxProps<Theme> = {
  p: 0,
  mt: `calc(-1 * ${MODAL_HEADER_HEIGHT})`,
  pt: MODAL_HEADER_HEIGHT,
  scrollPaddingTop: `calc(${MODAL_HEADER_HEIGHT} + 0.5rem)`,
};

const MODAL_BODY_PADDING_TOP = 1;

const MODAL_BODY_PADDING_BOTTOM = 2.5;

export const modalBodySx: SxProps<Theme> = {
  px: 2.5,
  pt: MODAL_BODY_PADDING_TOP,
  pb: MODAL_BODY_PADDING_BOTTOM,
  fontSize: '0.8125rem',
  lineHeight: 1.55,
};

const MODAL_LIST_HEIGHT = 'min(50vh, 25rem)';

/**
 * A list's filter sits right under the header, so the list takes back the body's top padding.
 * It keeps one height while it filters; full screen, it fills the body down to its bottom padding.
 */
export const getModalListFrameSx = (theme: Theme, fullScreen: boolean) => ({
  marginTop: theme.spacing(-MODAL_BODY_PADDING_TOP),
  height: fullScreen
    ? `calc(100dvh - ${MODAL_HEADER_HEIGHT} - ${theme.spacing(MODAL_BODY_PADDING_BOTTOM)})`
    : MODAL_LIST_HEIGHT,
});

export const getModalFooterSx =
  (isScrollable: boolean): SxProps<Theme> =>
  (theme) => ({
    px: 2,
    pt: isScrollable ? 1.5 : 0.75,
    pb: 1.75,
    borderTop: '1px solid',
    borderTopColor: isScrollable
      ? alpha(theme.palette.custom.OV.sys.outlineVariant, 0.7)
      : 'transparent',
    transition: getEdgeTransition(theme, ['border-color', 'padding']),
    [REDUCED_MOTION]: { transition: 'none' },
  });

const actionButtonBaseSx: SystemStyleObject<Theme> = {
  minWidth: 0,
  px: 1.75,
  py: 1,
  borderRadius: '0.5625rem',
  fontSize: '0.71875rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [`&.${buttonClasses.disabled}:not(.${buttonClasses.loading})`]: {
    opacity: DISABLED_OPACITY,
  },
  [`& .${buttonClasses.startIcon} > *:nth-of-type(1)`]: { fontSize: '0.75rem' },
  [`& .${buttonClasses.loadingIndicator}`]: { left: '0.625rem' },
};

export const getActionButtonSx =
  (kind: ModalActionKind, hasDanger: boolean): SxProps<Theme> =>
  (theme) => {
    const { error, secondaryText, secondaryFill, onSecondaryFill, surfaceContainerLowest } =
      theme.palette.custom.OV.sys;

    if (kind === 'secondary') {
      const ink = hasDanger ? error : secondaryText;

      return {
        ...actionButtonBaseSx,
        ...getKeyboardFocusRingSx(theme),
        color: ink,
        '&:hover': { backgroundColor: alpha(ink, 0.12) },
        [`&.${buttonClasses.disabled}`]: { color: ink },
      };
    }

    const [fill, onFill] =
      kind === 'danger' ? [error, surfaceContainerLowest] : [secondaryFill, onSecondaryFill];

    return {
      ...actionButtonBaseSx,
      ...getKeyboardFocusRingSx(theme),
      px: 2,
      [`& .${buttonClasses.loadingIndicator}`]: { left: '0.75rem' },
      color: onFill,
      backgroundColor: fill,
      '&:hover': { backgroundColor: fill, filter: 'brightness(1.07)' },
      [`&.${buttonClasses.disabled}`]: { color: onFill, backgroundColor: fill },
    };
  };
